import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, Clock, Plus } from 'lucide-react';
import { FlatRateItem, FlatRateCategory, createFlatRateItem, updateFlatRateItem, getFlatRateCategories, createFlatRateCategory } from '../lib/flat-rates';
import { selectInputContentOnFocus } from '../lib/utils';

interface FlatRateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  businessId?: string;
  item?: FlatRateItem | null;
  mode: 'create' | 'edit';
}

export default function FlatRateModal({ 
  isOpen, 
  onClose, 
  onSuccess,
  businessId,
  item,
  mode 
}: FlatRateModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<FlatRateCategory[]>([]);
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    hours: '',
    category_id: ''
  });

  const handleAddCategory = async () => {
    if (!businessId || !newCategoryName.trim()) return;

    try {
      setLoading(true);
      const newCategory = await createFlatRateCategory({
        business_id: businessId,
        name: newCategoryName.trim(),
        description: '',
        order_index: categories.length
      });
      
      await loadCategories();
      setFormData(prev => ({ ...prev, category_id: newCategory.id }));
      setNewCategoryName('');
      setIsAddingCategory(false);
    } catch (err) {
      console.error('Error creating category:', err);
      setError(err instanceof Error ? err.message : 'Failed to create category');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    if (!businessId) return;
    
    try {
      const data = await getFlatRateCategories(businessId);
      setCategories(data);
    } catch (err) {
      console.error('Error loading categories:', err);
      setError(err instanceof Error ? err.message : 'Failed to load categories');
    }
  };

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      loadCategories();
      if (mode === 'edit' && item) {
        setFormData({
          name: item.name,
          hours: item.hours.toString(),
          category_id: item.category_id || ''
        });
      }
      
      // Auto-focus on name field
      setTimeout(() => {
        const nameInput = document.querySelector('[data-flatrate-name-input]') as HTMLInputElement;
        if (nameInput) {
          nameInput.focus();
        }
      }, 100);
    }
  }, [isOpen, item, mode]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setFormData({
        name: '',
        hours: '',
        category_id: ''
      });
      setError(null);
      setIsAddingCategory(false);
      setNewCategoryName('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!businessId) {
      setError('No business selected');
      return;
    }

    // Validation
    if (!formData.name.trim()) {
      setError('Item name is required');
      return;
    }

    const hours = parseFloat(formData.hours);
    if (isNaN(hours) || hours < 0) {
      setError('Please enter valid hours');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (mode === 'create') {
        await createFlatRateItem({
          business_id: businessId,
          name: formData.name.trim(),
          description: '',
          category_id: formData.category_id || null,
          hours,
          order_index: 0
        });
      } else if (mode === 'edit' && item) {
        await updateFlatRateItem(item.id, {
          name: formData.name.trim(),
          category_id: formData.category_id || null,
          hours
        });
      }

      onSuccess();
    } catch (err) {
      console.error('Error saving flat rate item:', err);
      setError(err instanceof Error ? err.message : 'Failed to save flat rate item');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  const modalContent = (
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-[999999]`}
      onClick={onClose}
    >
      <div 
        className={`fixed inset-x-0 bottom-0 sm:inset-y-0 sm:right-0 sm:left-auto w-full sm:w-[600px] bg-white transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-y-0 sm:translate-x-0' : 'translate-y-full sm:translate-y-0 sm:translate-x-full'
        } rounded-t-2xl sm:rounded-none`}
        onTransitionEnd={handleTransitionEnd}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col max-h-[85vh] sm:max-h-full">
          {/* Gray header */}
          <div className="px-4 sm:px-6 py-4 sm:py-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between rounded-t-2xl sm:rounded-none">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
              {mode === 'create' ? 'New Flat Rate Item' : 'Edit Flat Rate Item'}
            </h2>
            <button 
              onClick={onClose} 
              className="p-2 text-gray-400 hover:text-gray-600 rounded-xl hover:bg-gray-100 transition-all duration-200 active:scale-95 touch-manipulation"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 sm:p-6">
            <div className="space-y-6 sm:space-y-8">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2 sm:mb-3">
                  Item Name
                </label>
                <input
                  type="text"
                  required
                  data-flatrate-name-input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full h-11 sm:h-12 rounded-xl border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm sm:text-base transition-all duration-200"
                  placeholder="Enter item name"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2 sm:mb-3">
                  Category
                </label>
                {isAddingCategory ? (
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-2">
                    <input
                      type="text"
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      className="flex-1 h-11 sm:h-12 rounded-xl border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm sm:text-base transition-all duration-200"
                      placeholder="Enter category name"
                    />
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={() => {
                          setIsAddingCategory(false);
                          setNewCategoryName('');
                        }}
                        className="flex-1 sm:flex-none px-4 py-2.5 sm:py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-all duration-200 active:scale-95"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleAddCategory}
                        disabled={!newCategoryName.trim() || loading}
                        className="flex-1 sm:flex-none px-4 py-2.5 sm:py-2 text-sm font-semibold text-white bg-purple-700 rounded-xl hover:bg-purple-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 active:scale-95"
                      >
                        {loading ? 'Creating...' : 'Add'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-2">
                    <select
                      value={formData.category_id}
                      onChange={(e) => setFormData(prev => ({ ...prev, category_id: e.target.value }))}
                      className="flex-1 h-11 sm:h-12 rounded-xl border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm sm:text-base transition-all duration-200"
                    >
                      <option value="">No Category</option>
                      {categories.map(category => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setIsAddingCategory(true)}
                      className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2.5 sm:py-2 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-200 active:scale-95"
                    >
                      <Plus className="h-4 w-4 mr-1.5" />
                      New Category
                    </button>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2 sm:mb-3">
                  Hours
                </label>
                <div className="relative">
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.1"
                    value={formData.hours}
                    onChange={(e) => setFormData(prev => ({ ...prev, hours: e.target.value }))}
                    onFocus={selectInputContentOnFocus}
                    className="w-full h-11 sm:h-12 rounded-xl border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm sm:text-base transition-all duration-200 pr-12"
                    placeholder="Enter hours"
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <Clock className="h-5 w-5 text-gray-400" />
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 sm:mt-8 pb-4 sm:pb-0">
              <button
                type="submit"
                disabled={loading || !formData.name.trim() || !formData.hours}
                className="w-full px-4 py-3 sm:py-3 text-sm sm:text-base font-semibold text-white bg-purple-700 rounded-xl hover:bg-purple-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 active:scale-95"
              >
                {loading ? 'Saving...' : mode === 'create' ? 'Create Item' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  // Use React portal to render modal at document.body level for proper z-index
  return ReactDOM.createPortal(modalContent, document.body);
}